import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useT } from '../../i18n/LanguageContext';
import service, {
  resourceOperationId,
  resourceError,
  verifiedResourceBlob,
} from '../../services/resourceCenterService';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import ResourceDialog, { ResourceMedia } from './ResourceDialog';
const metadata = (resource) => ({
  title: resource?.title || '',
  description: resource?.description || '',
  category: resource?.category || 'product_information',
});
export default function ResourceEditor({
  resource,
  config,
  onClose,
  onSaved,
  returnFocus,
  distributorId,
  distributorAudience = false,
}) {
  const t = useT();
  const scopeArgs = useMemo(() => (distributorId ? [distributorId] : []), [distributorId]);
  const [record, setRecord] = useState(resource);
  const [fields, setFields] = useState(metadata(resource));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [reviewed, setReviewed] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const keepEditing = useRef(null);
  const confirmationTrigger = useRef(null);
  const session = useRef(captureSession());
  const alive = useRef(true);
  const previewRef = useRef(null);
  const upload = useRef(null);
  const operation = useRef(null);
  const valid = () => alive.current && isSessionCurrent(session.current);
  const dirty = !!file || JSON.stringify(fields) !== JSON.stringify(metadata(record));
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (previewRef.current) URL.revokeObjectURL(previewRef.current.url);
    };
  }, []);
  useEffect(() => {
    const warn = (e) => {
      if (dirty || uncertain) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, uncertain]);
  const clearPreview = () => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current.url);
    previewRef.current = null;
    setPreview(null);
    setReviewed(false);
  };
  const askToDiscard = (action, event) => {
    confirmationTrigger.current = event?.currentTarget || document.activeElement;
    setConfirmation(action);
  };
  const cancelDiscard = () => {
    setConfirmation(null);
    requestAnimationFrame(
      () => confirmationTrigger.current?.isConnected && confirmationTrigger.current.focus()
    );
  };
  const close = (event) => {
    if (dirty || uncertain) askToDiscard('close', event);
    else onClose();
  };
  useEffect(() => {
    if (confirmation) keepEditing.current?.focus();
  }, [confirmation]);
  const change = (key, value) => {
    clearPreview();
    setError('');
    setFields((old) => ({ ...old, [key]: value }));
  };
  const fail = (failure, uploading = false) => {
    if (!valid()) return;
    setError(
      t(
        uploading && failure?.response?.status === 400
          ? 'This file could not be processed. Choose a static PDF, PNG or JPEG without forms or active content.'
          : resourceError(failure)
      )
    );
    setConflict(failure?.response?.status === 409);
    if (failure?.response?.status < 500 && failure.response.status !== 409)
      operation.current = null;
    setUncertain(!failure?.response || failure.response.status >= 500);
  };
  const showPreview = async (next) => {
    const response = await service.preview(next.id, next.draftRevisionId, ...scopeArgs);
    if (!valid()) return;
    const blob = verifiedResourceBlob(response, next);
    if (
      String(response.headers?.['x-resource-revision']) !== String(next.revision) ||
      response.headers?.['x-resource-snapshot'] !== next.draftRevisionId ||
      response.headers?.['x-resource-sha256'] !== next.sha256
    )
      throw new Error('Preview changed');
    clearPreview();
    const item = {
      url: URL.createObjectURL(blob),
      snapshotId: next.draftRevisionId,
      sha256: next.sha256,
      revision: next.revision,
      mimeType: blob.type,
    };
    previewRef.current = item;
    setPreview(item);
  };
  const save = async (e) => {
    e?.preventDefault();
    if (busy) return;
    if (!fields.title.trim() || (!record && !file)) {
      setError(t('Add a title and choose a document.'));
      return;
    }
    setError('');
    setBusy('save');
    let phase = 'save';
    try {
      let receipt = upload.current?.receipt;
      if (file && !receipt) {
        if (!upload.current) upload.current = { id: resourceOperationId(), receipt: null };
        phase = 'upload';
        receipt = await service.upload(
          file,
          upload.current.id,
          (value) => {
            if (valid()) setProgress(value);
          },
          ...scopeArgs
        );
        if (!valid()) return;
        upload.current.receipt = receipt;
        phase = 'save';
      }
      let next = record;
      if (dirty || !record || !record.draftRevisionId || operation.current?.kind === 'save') {
        if (!operation.current)
          operation.current = {
            kind: 'save',
            id: record?.id,
            body: {
              ...fields,
              title: fields.title.trim(),
              description: fields.description.trim(),
              productFamilyId: record?.productFamilyId || null,
              operationId: resourceOperationId(),
              ...(record ? { revision: record.revision } : {}),
              ...(receipt ? { uploadId: receipt.id } : {}),
            },
          };
        next = await service.save(operation.current.id, operation.current.body, ...scopeArgs);
        if (!valid()) return;
        operation.current = null;
        upload.current = null;
        setRecord(next);
        setFields(metadata(next));
        setFile(null);
        setUncertain(false);
        onSaved(next, 'draft');
      }
      await showPreview(next);
    } catch (failure) {
      fail(failure, phase === 'upload');
    } finally {
      if (valid()) setBusy('');
    }
  };
  const publish = async () => {
    if (!preview || !reviewed || dirty || busy) return;
    setBusy('publish');
    setError('');
    try {
      if (!operation.current)
        operation.current = {
          kind: 'publish',
          body: {
            operationId: resourceOperationId(),
            revision: preview.revision,
            snapshotId: preview.snapshotId,
            sha256: preview.sha256,
          },
        };
      const next = await service.publish(record.id, operation.current.body, ...scopeArgs);
      if (valid()) {
        operation.current = null;
        setUncertain(false);
        onSaved(next, 'published');
        onClose();
      }
    } catch (failure) {
      fail(failure);
    } finally {
      if (valid()) setBusy('');
    }
  };
  const reload = async () => {
    if (!record) return;
    setConfirmation(null);
    setBusy('reload');
    try {
      const next = await service.get(record.id, true, ...scopeArgs);
      if (valid()) {
        operation.current = null;
        upload.current = null;
        setRecord(next);
        setFields(metadata(next));
        setFile(null);
        clearPreview();
        setError('');
        setConflict(false);
        setUncertain(false);
        onSaved(next, 'reloaded');
      }
    } catch (failure) {
      fail(failure);
    } finally {
      if (valid()) setBusy('');
    }
  };
  const choose = (e) => {
    const next = e.target.files?.[0];
    if (!next) return;
    if (
      !/\.(pdf|png|jpe?g)$/i.test(next.name) ||
      !['application/pdf', 'image/png', 'image/jpeg'].includes(next.type) ||
      next.size < 1 ||
      next.size > config.limits.fileBytes
    ) {
      setError(t('Choose a PDF, PNG or JPEG file no larger than 5 MB.'));
      e.target.value = '';
      return;
    }
    upload.current = null;
    operation.current = null;
    clearPreview();
    setFile(next);
    setError('');
    setProgress(0);
  };
  return (
    <ResourceDialog
      returnFocus={returnFocus}
      wide={!confirmation}
      title={t(
        confirmation === 'reload'
          ? 'Reload document?'
          : confirmation
            ? 'Discard unsaved changes?'
            : record
              ? 'Edit document'
              : 'Add document'
      )}
      onClose={confirmation ? cancelDiscard : close}
      busy={!!busy}
    >
      {confirmation && (
        <div className="rc-confirmation">
          <p>
            {t(
              uncertain
                ? 'A save may have completed. Check Drafts before adding this document again. Closing does not remove saved or published documents.'
                : 'Only your unsaved edits will be discarded. Published documents are unchanged.'
            )}
          </p>
          {confirmation === 'reload' && (
            <p>{t('The latest saved version will replace the fields in this editor.')}</p>
          )}
          <div className="rc-actions">
            <button ref={keepEditing} type="button" onClick={cancelDiscard}>
              {t('Keep editing')}
            </button>
            <button
              type="button"
              className="rc-primary"
              onClick={() => (confirmation === 'reload' ? reload() : onClose())}
            >
              {t(confirmation === 'reload' ? 'Discard & reload' : 'Discard changes')}
            </button>
          </div>
        </div>
      )}
      <div hidden={!!confirmation}>
        <div className="rc-editor">
          <form onSubmit={save} className="rc-editor-form">
            <fieldset disabled={!!busy || uncertain || conflict}>
              <label>
                {t('Title')}
                <input
                  value={fields.title}
                  maxLength={config.limits.title}
                  onChange={(e) => change('title', e.target.value)}
                  required
                />
              </label>
              <label>
                {t('Description')}
                <textarea
                  rows="3"
                  value={fields.description}
                  maxLength={config.limits.description}
                  onChange={(e) => change('description', e.target.value)}
                />
              </label>
              <label>
                {t('Category')}
                <select
                  value={fields.category}
                  onChange={(e) => change('category', e.target.value)}
                >
                  {config.categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {t(category.label)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="rc-upload-label">
                {t(record ? 'Replace file (optional)' : 'Document file')}
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={choose}
                  disabled={!config.uploadsEnabled}
                />
              </label>
              <p className="rc-muted rc-filename">
                {file?.name || record?.fileName || t('PDF, PNG or JPEG · maximum 5 MB')}
              </p>
              {!config.uploadsEnabled && (
                <p className="rc-callout">
                  {t(
                    'Uploads are currently unavailable. Existing document descriptions can still be edited.'
                  )}
                </p>
              )}
            </fieldset>
            {busy === 'save' && file && (
              <p role="status">
                {t('Uploading and preparing document… {progress}%', { progress })}
              </p>
            )}
            {error && (
              <div className="rc-error" role="alert">
                {error}
              </div>
            )}
            {conflict && record && (
              <button
                type="button"
                onClick={(event) => askToDiscard('reload', event)}
                disabled={!!busy}
              >
                {t('Reload document')}
              </button>
            )}
            <div className="rc-editor-actions">
              <button
                className="rc-primary"
                type="submit"
                disabled={
                  !!busy || conflict || (uncertain && operation.current?.kind === 'publish')
                }
              >
                {t(
                  busy === 'save'
                    ? 'Preparing preview…'
                    : uncertain && operation.current?.kind !== 'publish'
                      ? 'Retry save'
                      : 'Save & preview'
                )}
              </button>
              <button type="button" disabled={!!busy} onClick={close}>
                {t('Close')}
              </button>
            </div>
            <p className="rc-muted">
              {t(
                'Saving creates a private draft. The current published version stays available until you publish a replacement.'
              )}
            </p>
          </form>
          <section className="rc-editor-review" aria-label={t('Publication review')}>
            {preview ? (
              <>
                <ResourceMedia
                  key={preview.url}
                  url={preview.url}
                  mimeType={preview.mimeType}
                  title={fields.title}
                />
                <label className="rc-check">
                  <input
                    type="checkbox"
                    checked={reviewed}
                    onChange={(e) => setReviewed(e.target.checked)}
                    disabled={!!busy || conflict}
                  />
                  {t(
                    distributorAudience
                      ? 'I reviewed this processed document and its description. It contains general resources, no patient or private clinic records, and can be shared with users in this distributor workspace.'
                      : 'I reviewed this processed document and its description. It contains general resources, no patient or private clinic records, and can be shared with all users.'
                  )}
                </label>
                <button
                  type="button"
                  className="rc-primary"
                  disabled={!reviewed || dirty || !!busy || conflict}
                  onClick={publish}
                >
                  {t(
                    busy === 'publish'
                      ? 'Publishing…'
                      : uncertain
                        ? 'Retry publish'
                        : distributorAudience
                          ? 'Publish for this distributor'
                          : 'Publish for all users'
                  )}
                </button>
              </>
            ) : (
              <div className="rc-preview-empty">
                <span className="material-symbols-outlined" aria-hidden="true">
                  preview
                </span>
                <h3>{t('Review before publishing')}</h3>
                <p>
                  {t(
                    'Save your draft to preview the processed file here. Nothing is shared until you publish.'
                  )}
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    </ResourceDialog>
  );
}
